import { DestroyRef, inject, Injectable } from '@angular/core';
import { ContestSettings } from '../models/contest-settings.models';
import { SupabaseDbContestSettingsService } from '../../supabase/supabase-db-contest-settings.service';
import { ContestSettingsStore } from '../stores/contest-settings.store';
import { fromApi } from '../utils/case-convert';
import { SupabaseDbProjectServiceService } from '../../supabase/supabase-db-projects.service';
import { ProjectStatesCountByYear } from '../models/projects.models';
import { REALTIME_SUBSCRIBE_STATES, RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { supabase } from '../../supabase/supabase.client';
import { TABLE_NAMES } from '../../supabase/supabase.constants';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ContestSettingsService {
  private readonly contestSettingsDb = inject(SupabaseDbContestSettingsService);
  private readonly projectsDb = inject(SupabaseDbProjectServiceService);
  private readonly store = inject(ContestSettingsStore);
  private readonly destroyRef = inject(DestroyRef);

  private channelName: string = 'contest_settings_changes';
  private channel: RealtimeChannel | null = null;

  constructor() {
    this.destroyRef.onDestroy(async () => {
      await this.unsubscribeFromContestSettingsChanges();
    });
  }

  public async initializeContestSettings(): Promise<void> {
    const {data, error} = await this.contestSettingsDb.fetchContestSettings();

    if (error) {
      console.error('Error fetching contest settings:', error);
      return;
    }
    if (!data) {
      console.error('Contest settings request succeeded but returned no data.');
    } else {
      this.setContestSettings(data);
    }
    void this.subscribeContestSettingsChanges();
  }

  public async checkProjectsBeforeAdminPhaseThree(year: number): Promise<ProjectStatesCountByYear> {
    const data = await this.projectsDb.getProjectStatesCountByYear(year);
    if (!data) {
      throw new Error('No project data found for year ' + year);
    }
    return fromApi(data);
  }

  private setContestSettings(data: unknown): void {
    this.store.contestSettings = fromApi<ContestSettings>(data);
  }

  private async subscribeContestSettingsChanges(): Promise<void> {
    if (this.channel) {
      await this.unsubscribeFromContestSettingsChanges();
    }

    this.channel = supabase.channel(this.channelName)
      .on('postgres_changes', {
          event: 'UPDATE',
          schema: 'public',
          table: TABLE_NAMES.CONTEST_SETTINGS,
          filter: 'singleton=eq.true',
        },
        (payload: RealtimePostgresChangesPayload<{ [p: string]: any }>) => {
          if (payload.new) {
            this.setContestSettings(payload.new);
          }
        },
      )
      .subscribe(async (status: REALTIME_SUBSCRIBE_STATES) => {
        if (environment.production) return;

        switch (status) {
          case REALTIME_SUBSCRIBE_STATES.SUBSCRIBED:
            console.log('[Contest Settings Realtime] Successfully subscribed to changes.');
            break;
          case REALTIME_SUBSCRIBE_STATES.CHANNEL_ERROR:
            console.error('[Contest Settings Realtime] Channel error.');
            break;
          case REALTIME_SUBSCRIBE_STATES.TIMED_OUT:
            console.error('[Contest Settings Realtime] Subscription timed out.');
            break;
          case REALTIME_SUBSCRIBE_STATES.CLOSED:
            console.log('[Contest Settings Realtime] Subscription closed.');
            break;
        }
      });
  };

  private async unsubscribeFromContestSettingsChanges(): Promise<void> {
    if (this.channel) {
      await supabase.removeChannel(this.channel);
      this.channel = null;
    }
  }
}
